import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { AuthService, StateService, BreadcrumbService, Utils, CompanyService } from 'src/app/services';
import { 
  AssignReleaseIssueTagRequest, AssignReleaseIssueTagResponse, GetReleaseRequest, GetReleaseResponse, ListReleaseIssuesRequest, ListReleaseIssuesResponse, ListReleasesRequest, ListReleasesResponse, ListTagsRequest, ListTagsResponse, ProductName, 
  Release, 
  ReleaseIssue, ReleaseIssueCategory, ReleaseIssuePriority, ReleaseIssueStatus, ReleaseIssueTag, RemoveReleaseIssueTagRequest, RemoveReleaseIssueTagResponse, ResourceType, SituViewWebApiClient, Tag 
} from 'situview-webapi-client';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormControl } from '@angular/forms';
import { ReleaseIssueDialogComponent } from '../../admin/product-releases/release-issue-dialog/release-issue-dialog.component';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';

interface HouseReport {
  name: string;
  totalIssues: number;
  tags: { [key: string]: number };
}

@Component({
  selector: 'release-issues-report',
  templateUrl: './release-issues.component.html',
  styleUrls: ['./release-issues.component.scss'],
})
export class ReleaseIssuesReportComponent extends SituBaseComponent {
  @Input() hideEdit: boolean = true;
  @Input() hideReport: boolean = true;
  @Input() isSituUser: boolean = false;
  @Input() isSelectable: boolean = false;
  @Input() displayLength: number = 3;
  @Input() selectedReleaseIssue: string | undefined;
  @Input() productName: ProductName | undefined;
  @Output() onSelectClick: EventEmitter<string> = new EventEmitter();
  @Output() onIssueSelectClick: EventEmitter<string> = new EventEmitter();

  /* Query Parameters */
  readonly ReleaseParamName: string = "release";
  readonly PriorityParamName: string = "priority";
  readonly StatusParamName: string = "status";
  readonly CategoryParamName: string = "category";
  readonly TagParamName: string = "tag";
  readonly CreatedParamName: string = "created";
  readonly AssignedParamName: string = "assigned";
  readonly MyIssuesParamName: string = "myIssues";
  readonly OpenOnlyParamName: string = "openOnly";
  readonly SearchParamName: string = "search";
  readonly HomeParamName: string = "home";
  /* Query Parameters */

  // Base columns that will always be shown
  baseColumns: string[] = ['name', 'totalIssues'];
  
  // Tag columns will be populated dynamically
  tagColumns: string[] = [];
  
  // Combined columns for the table
  displayedColumns: string[] = [...this.baseColumns];
  
  // Data source for the houses report table
  houseReportDataSource = new MatTableDataSource<HouseReport>([]);
  
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    authService: AuthService,
    stateService: StateService,
    private situViewApiClient: SituViewWebApiClient,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private breadcrumbService: BreadcrumbService,
    private route: ActivatedRoute,
    private router: Router,
    private companyService: CompanyService) {
    super(authService, stateService);

    // Read filters from URL query parameters
    const queryParams = this.route.snapshot.queryParams;
    
    if (queryParams[this.ReleaseParamName]) {
      this.selectedReleaseId = queryParams[this.ReleaseParamName];
    }
    
    if (queryParams[this.PriorityParamName]) {
      this.priorityFilter = Number(queryParams[this.PriorityParamName]);
    }
    
    if (queryParams[this.StatusParamName]) {
      this.statusFilter = Number(queryParams[this.StatusParamName]);
    }
    
    if (queryParams[this.CategoryParamName]) {
      this.categoryFilter = Number(queryParams[this.CategoryParamName]);
    }
    
    if (queryParams[this.TagParamName]) {
      this.tagFilter = queryParams[this.TagParamName];
    }
    
    if (queryParams[this.CreatedParamName]) {
      this.createdFilter = queryParams[this.CreatedParamName];
    }
    
    if (queryParams[this.AssignedParamName]) {
      this.assignedFilter = queryParams[this.AssignedParamName];
    }
    
    if (queryParams[this.MyIssuesParamName]) {
      this.showMyIssuesOnly = queryParams[this.MyIssuesParamName] === 'true';
    }
    
    if (queryParams[this.OpenOnlyParamName]) {
      this.showOpenIssuesOnly = queryParams[this.OpenOnlyParamName] === 'true';
    } else {
      // Default to true if not specified
      this.showOpenIssuesOnly = true;
    }
    
    if (queryParams[this.SearchParamName]) {
      this.searchReleaseIssuesField.setValue(queryParams[this.SearchParamName]);
    }
    
    if (queryParams[this.HomeParamName]) {
      this.homeFilter = queryParams[this.HomeParamName];
    }
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngAfterViewInit() {
    this.houseReportDataSource.sort = this.sort;
  }

  protected onSelectedCompanyChange(): void {
    this.breadcrumbService.setParam("release", "No Release Selected");
    this.release = undefined;
    this.tags = undefined;
    this.releaseIssues = [];
    this.filteredReleaseIssues = [];
    this.processReleaseData();
    this.loadRelease()
      .then(() => {
        this.loadTags()
          .then(() => {
            this.loadReleaseIssues()
              .then(() => {
                this.processHouseReport();
              });
          });
      });
  }

  selectedReleaseId: string | undefined;
  release: Release | undefined;
  releases: Release[] = [];
  private loadRelease() {
    let listReleaseRequest = new ListReleasesRequest();
    listReleaseRequest.companyId = this.selectedCompany?.id;

    return this.situViewApiClient.listReleases(listReleaseRequest)
      .then((listReleaseResponse: ListReleasesResponse) => {
        if (listReleaseResponse.success && listReleaseResponse.items) {
          this.releases = listReleaseResponse.items;
          if (this.selectedReleaseId) {
            return this.loadReleaseData(this.selectedReleaseId);
          } else {
            let releaseId = listReleaseResponse.items?.sort(x => x.releaseItems?.length ?? 0)?.find(x => true)?.id;
            if (releaseId) {
              this.selectedReleaseId = releaseId;
              return this.loadReleaseData(releaseId);
            }
          }
        }
      });
  }

  onReleaseChange() {
    if (this.selectedReleaseId) {
      this.loadReleaseData(this.selectedReleaseId);
    }
  }

  private loadReleaseData(releaseId: string) {
    let request = new GetReleaseRequest();
    request.companyId = this.selectedCompany?.id;
    request.releaseId = releaseId;
    
    return this.situViewApiClient.getRelease(request)
      .then((response: GetReleaseResponse) => {
        if (response.success && response.release?.name) {
          if (response?.release?.companyId != this.selectedCompany?.id && response?.release?.companyId) {
            if (!this.authService.isAuthorizedForCompanyById(response.release.companyId)) {
              this.showMessage(`Sorry! Your are not authorized for company ${response.release.companyId}.`);
              this.router.navigateByUrl('/unauthorized');
              return;
            }

            this.companyService.getCompanyById(response.release.companyId)
              .then((company) => {
                if (!company) return;

                this.stateService.state = {
                  ...this.stateService.state,
                  company
                };
              });
          }

          this.updateParams();
          this.breadcrumbService.setParam("release", response.release.name);
          this.release = response.release;
          this.loadReleaseIssues();
        }
      });
  }

  searchReleaseIssuesField = new FormControl();
  releaseIssues: ReleaseIssue[] = [];
  filteredReleaseIssues: ReleaseIssue[] | undefined;
  confirmedNewIssue: boolean = false;
  releaseIssueStatuses = ReleaseIssueStatus;
  releaseIssuePriorities = ReleaseIssuePriority;
  releaseIssueCategories = ReleaseIssueCategory;
  
  private loadReleaseIssues() {
    if (!this.selectedCompany?.id) {
      this.showMessage("Company is required to fetch Release Issues!")
      return Promise.reject();
    }
    let request = new ListReleaseIssuesRequest();
    request.companyId = this.selectedCompany.id;
    request.releaseId = this.release?.id;
    request.includeStats = true;
    if (this.productName !== undefined) request.productName = this.productName;

    return this.situViewApiClient.listReleaseIssues(request)
      .then((response: ListReleaseIssuesResponse) => {
        if (response.success && response.items) {
          this.releaseIssues = response.items;
          // Apply initial filtering based on current filter settings
          this.doReleaseIssueFilter(this.searchReleaseIssuesField?.value || '');
        }
      })
      .catch((err: any) => {

      })
      .finally(() => {
        //this.loading = false;
      });
  }

  private processReleaseData() {
    this.calculateStatusPieChart();
    this.calculateHomeProgressPieChart();
    this.processHouseReport();
    this.calculatePriorityTypesPieChart();
    this.calculateIssueCategoriesPieChart();
    this.calculateIssueStatusesPieChart();
  }

  // New method to process and group release issues by house
  private processHouseReport() {
    if (!this.filteredReleaseIssues || !this.tags) {
      return;
    }
  
    // Extract unique home names from release items
    const uniqueHomes = new Set<string>();
    const uniqueTags = new Set<string>();
    
    // Get all unique homes from release items
    if (this.release?.releaseItems) {
      this.release.releaseItems.forEach(item => {
        if (item.home) {
          uniqueHomes.add(item.home);
        }
      });
    }
  
    // Get all unique tags
    this.tags.forEach(tag => {
      if (tag.name) {
        uniqueTags.add(tag.name);
      }
    });
  
    // Initialize the houses data structure
    const houses: { [key: string]: any } = {};
    
    uniqueHomes.forEach(homeName => {
      houses[homeName] = {
        name: homeName,
        totalIssues: 0,
        tags: {},
        uniqueIssueIds: new Set<string>() // Track unique issue IDs
      };
      
      // Initialize all tag counts to 0
      uniqueTags.forEach(tagName => {
        houses[homeName].tags[tagName] = 0;
      });
    });
  
    // Process each issue only once
    this.filteredReleaseIssues.forEach(issue => {
      if (!issue.id) return;
      
      // Find all release items associated with this issue
      const associatedItems = this.release?.releaseItems
        ?.filter(item => item.releaseIssueReleaseItems?.some(ri => ri.releaseIssueId === issue.id));
      
      if (associatedItems && associatedItems.length > 0) {
        // Group by home name to count properly
        const issueHomes = new Set<string>();
        
        associatedItems.forEach(item => {
          if (item.home) {
            issueHomes.add(item.home);
            
            // Add this issue to the unique issues for this home
            if (houses[item.home]) {
              houses[item.home].uniqueIssueIds.add(issue.id);
            }
          }
        });
        
        // Now count this issue's tags for each home it belongs to
        issueHomes.forEach(homeName => {
          if (houses[homeName]) {
            // Count each tag for this issue
            if (issue.releaseIssueTags && issue.releaseIssueTags.length > 0) {
              issue.releaseIssueTags.forEach(issueTag => {
                const tag = this.tags?.find(t => t.id === issueTag.tagId);
                if (tag && tag.name) {
                  // Increment tag count only once per issue per home
                  houses[homeName].tags[tag.name]++;
                }
              });
            }
          }
        });
      }
    });
    
    // Set the total issues count to the number of unique issues
    Object.keys(houses).forEach(homeName => {
      houses[homeName].totalIssues = houses[homeName].uniqueIssueIds.size;
      // Remove the helper property before using in data source
      delete houses[homeName].uniqueIssueIds;
    });
  
    // Update the tag columns based on unique tags found in the data
    this.tagColumns = [...uniqueTags].sort();
    
    // Update displayed columns to include the dynamic tag columns
    this.displayedColumns = ['name', 'totalIssues', ...this.tagColumns];
    
    // Set the table data
    this.houseReportDataSource.data = Object.values(houses);
    
    // Update the issues types chart
    this.updateIssueTypesChart();
  }
  
  private updateIssueTypesChart() {
    if (!this.filteredReleaseIssues || !this.tags) {
      return;
    }
  
    // We need to count unique issues per tag
    const tagIssueIds: { [key: string]: Set<string> } = {};
    
    // Initialize a Set for each tag to track unique issue IDs
    this.tagColumns.forEach(tag => {
      tagIssueIds[tag] = new Set<string>();
    });
    
    // Count unique issues per tag across all release issues
    this.filteredReleaseIssues.forEach(issue => {
      if (!issue.id) return;
      
      // Count the issue for each of its tags
      if (issue.releaseIssueTags && issue.releaseIssueTags.length > 0) {
        issue.releaseIssueTags.forEach(issueTag => {
          const tag = this.tags?.find(t => t.id === issueTag.tagId);
          if (tag && tag.name && tagIssueIds[tag.name]) {
            // Add this issue ID to the set for this tag
            if (issue.id) {
              tagIssueIds[tag.name].add(issue.id);
            }
          }
        });
      }
    });
    
    // Create sorted arrays of labels and data, filtering out tags with no issues
    const allTags = Object.keys(tagIssueIds).sort();
    const tagsWithIssues = allTags.filter(tag => tagIssueIds[tag].size > 0);
    const sortedTags = tagsWithIssues;
    const sortedCounts = sortedTags.map(tag => tagIssueIds[tag].size);
    
    // Create a color array that corresponds directly to the tag labels
    const tagColors: string[] = [];
    
    // For each tag, find its corresponding color
    sortedTags.forEach((tagName, index) => {
      const tag = this.tags?.find(t => t.name === tagName);
      if (tag?.backgroundColour) {
        // Use tag's background color if available
        const color = Utils.hexToRgba(tag.backgroundColour, 1);
        tagColors.push(color);
      } else {
        // Generate cohesive purple shades for tags without colors
        const lightness = 70 - (index * 8); // Start at 70% and decrease by 8% per tag
        const color = `hsl(280, 70%, ${Math.max(lightness, 25)}%)`;
        tagColors.push(color);
      }
    });
    
    // Update the chart data - pie chart format
    this.issueTypesLabels = sortedTags;
    this.issueTypesData = [{
      data: sortedCounts,
      label: 'Issue Types'
    }];
    
    // Set the colors array for the chart
    this.issueTypesColors = tagColors;

    // this.issueTypesOptions = {
    //   responsive: true,
    //   maintainAspectRatio: true,
    //   legend: {
    //     display: true,
    //     position: 'top'
    //   },
    //   scales: {
    //     xAxes: [{
    //       barPercentage: 0.9,    // Make bars take up 90% of available width
    //       categoryPercentage: 1, // Make categories take up all available space
    //       gridLines: {
    //         display: true
    //       }
    //     }],
    //     yAxes: [{
    //       ticks: {
    //         beginAtZero: true
    //       }
    //     }]
    //   }
    // };

    this.issueTypesLoading = false;
  }

  // Helper method to get a tag count for a given row
  getTagCount(row: HouseReport, tag: string): number {
    return row.tags[tag] || 0;
  }

  // Helper method to get total issue count for the breakdown table
  getTotalIssueCount(): number {
    if (!this.issueTypesData || !this.issueTypesData[0] || !this.issueTypesData[0].data) {
      return 0;
    }
    return this.issueTypesData[0].data.reduce((total: number, count: number) => total + count, 0);
  }

  doReleaseIssueFilter = (value: string = '') => {
    const searchTerm = value.trim().toLowerCase();
  
    this.filteredReleaseIssues = this.releaseIssues?.filter((issue) => {
      const matchesSearch = 
        issue.description?.toLowerCase().includes(searchTerm) || 
        issue.title?.toLowerCase().includes(searchTerm) ||
        issue.createdByName?.toLowerCase().includes(searchTerm) ||
        issue.assignedToName?.toLowerCase().includes(searchTerm);
      const matchesPriority = !this.priorityFilter || issue.releaseIssuePriority === this.priorityFilter;
      const matchesStatus = !this.statusFilter || issue.releaseIssueStatus === this.statusFilter;
      const matchesCreated = !this.createdFilter || issue.createdByName === this.createdFilter;
      const matchesAssigned = !this.assignedFilter || issue.assignedToName === this.assignedFilter;
      const matchesCategory = !this.categoryFilter || issue.releaseIssueCategory === this.categoryFilter;
      
      // Check tag filter - issue must have a tag with the specified name
      const matchesTag = !this.tagFilter || (issue.releaseIssueTags?.some(issueTag => {
        const tag = this.tags?.find(t => t.id === issueTag.tagId);
        return tag?.name === this.tagFilter;
      }) ?? false);

      // Check "My Issues" filter - issue must be assigned to current user
      const matchesMyIssues = !this.showMyIssuesOnly || issue.assignedTo === this.authService.getUserId();

      // Check "Open Issues Only" filter - issue must not be completed
      const matchesOpenOnly = !this.showOpenIssuesOnly || (
        issue.releaseIssueStatus !== ReleaseIssueStatus.Complete && 
        issue.releaseIssueStatus !== ReleaseIssueStatus.CompleteNonIssue
      );

      // Check home filter - issue must be associated with the specified home
      const matchesHome = !this.homeFilter || (this.release?.releaseItems?.some(item => 
        item.home === this.homeFilter && 
        item.releaseIssueReleaseItems?.some(ri => ri.releaseIssueId === issue.id)
      ) ?? false);
  
      return matchesSearch && (matchesPriority && matchesStatus && matchesCreated && matchesAssigned && matchesCategory && matchesTag && matchesMyIssues && matchesOpenOnly && matchesHome);
    });

    // Update house report and other metrics after filtering
    this.processReleaseData();
  };

  getPriority(releaseIssuePriority: ReleaseIssuePriority | undefined) {
    switch(releaseIssuePriority) {
      case ReleaseIssuePriority.High:
        return "Blocker";
      case ReleaseIssuePriority.Medium:
        return "Medium";
      default: 
        return "Low";
    }
  }

  getCategory(releaseIssueCategory: ReleaseIssueCategory | undefined) {
    switch(releaseIssueCategory) {
      case ReleaseIssueCategory.Theming:
        return "Style / Theme";
      case ReleaseIssueCategory.Drawing:
        return "Home Plan";
      default: 
        return "Software";
    }
  }

  releaseIssueDialogRef: MatDialogRef<ReleaseIssueDialogComponent> | undefined;
  onCardSelectClick(releaseIssueId: string | undefined) { 
    this.releaseIssueDialogRef = this.dialog.open(ReleaseIssueDialogComponent, {
      panelClass: 'modal-panel',
      width: '60%',
      height: '80%',
      disableClose: true,
      data: {
        isSituUser: this.isSituUser,
        companyId: this.selectedCompany?.id,
        releaseIssueId: releaseIssueId
      }
    });
  }
  priorityFilter: ReleaseIssuePriority | undefined;
  onCardPriorityClick(releaseIssuePriority: ReleaseIssuePriority | undefined) { 
    if (releaseIssuePriority !== this.priorityFilter) {
      this.priorityFilter = releaseIssuePriority;
      this.doReleaseIssueFilter(this.searchReleaseIssuesField.value || '');
      this.updateParams();
    }
  }
  statusFilter: ReleaseIssueStatus | undefined;
  onCardStatusClick(releaseIssueStatus: ReleaseIssueStatus | undefined) { 
    if (releaseIssueStatus !== this.statusFilter) {
      this.statusFilter = releaseIssueStatus;
      this.doReleaseIssueFilter(this.searchReleaseIssuesField.value || '');
      this.updateParams();
    }
  }
  createdFilter: string | undefined;
  onCardCreatedClick(created: string | undefined) { 
    if (created !== this.createdFilter) {
      this.createdFilter = created;
      this.doReleaseIssueFilter(this.searchReleaseIssuesField.value || '');
      this.updateParams();
    }
  }
  assignedFilter: string | undefined;
  onCardAssignedClick(assigned: string | undefined) { 
    if (assigned !== this.assignedFilter) {
      this.assignedFilter = assigned;
      this.doReleaseIssueFilter(this.searchReleaseIssuesField.value || '');
      this.updateParams();
    }
  }
  categoryFilter: ReleaseIssueCategory | undefined;
  onCategoryChartClick(categoryLabel: string | undefined) {
    let category: ReleaseIssueCategory | undefined;
    
    if (categoryLabel) {
      switch (categoryLabel) {
        case 'Home Plan':
          category = ReleaseIssueCategory.Drawing;
          break;
        case 'Style / Theme':
          category = ReleaseIssueCategory.Theming;
          break;
        case 'Software':
          category = ReleaseIssueCategory.Software;
          break;
        default:
          category = undefined;
      }
    }
    
    if (category !== this.categoryFilter) {
      this.categoryFilter = category;
      this.doReleaseIssueFilter(this.searchReleaseIssuesField.value || '');
      this.updateParams();
    }
  }

  onPriorityChartClick(priorityLabel: string | undefined) {
    let priority: ReleaseIssuePriority | undefined;
    
    if (priorityLabel) {
      switch (priorityLabel) {
        case 'Blocker':
          priority = ReleaseIssuePriority.High;
          break;
        case 'Medium':
          priority = ReleaseIssuePriority.Medium;
          break;
        case 'Low':
          priority = ReleaseIssuePriority.Low;
          break;
        default:
          priority = undefined;
      }
    }
    
    if (priority !== this.priorityFilter) {
      this.priorityFilter = priority;
      this.doReleaseIssueFilter(this.searchReleaseIssuesField.value || '');
      this.updateParams();
    }
  }

  onStatusChartClick(statusLabel: string | undefined) {
    let status: ReleaseIssueStatus | undefined;
    
    if (statusLabel) {
      // Find the status enum value by matching the label
      const statusKeys = Object.keys(this.releaseIssueStatuses)
        .filter(key => !isNaN(Number(key))); // Filter out reverse mappings
      
      for (const statusKey of statusKeys) {
        const statusValue = Number(statusKey);
        const statusName = this.releaseIssueStatuses[statusValue];
        if (statusName === statusLabel) {
          status = statusValue;
          break;
        }
      }
    }
    
    if (status !== this.statusFilter) {
      this.statusFilter = status;
      this.doReleaseIssueFilter(this.searchReleaseIssuesField.value || '');
      this.updateParams();
    }
  }

  tagFilter: string | undefined;
  homeFilter: string | undefined;
  
  onHomeClick(homeName: string | undefined) {
    if (homeName !== this.homeFilter) {
      this.homeFilter = homeName;
      this.doReleaseIssueFilter(this.searchReleaseIssuesField.value || '');
      this.updateParams();
    }
  }
  
  onTagChartClick(tagLabel: string | undefined) {
    if (tagLabel !== this.tagFilter) {
      this.tagFilter = tagLabel;
      this.doReleaseIssueFilter(this.searchReleaseIssuesField.value || '');
      this.updateParams();
    }
  }

  // Permanent filter pills
  showMyIssuesOnly: boolean = false;
  showOpenIssuesOnly: boolean = true; // Default to true

  toggleMyIssuesFilter() {
    this.showMyIssuesOnly = !this.showMyIssuesOnly;
    this.doReleaseIssueFilter(this.searchReleaseIssuesField.value || '');
    this.updateParams();
  }

  toggleOpenIssuesFilter() {
    this.showOpenIssuesOnly = !this.showOpenIssuesOnly;
    this.doReleaseIssueFilter(this.searchReleaseIssuesField.value || '');
    this.updateParams();
  }
  onCardIssueLoadClick(issueUrl: string | undefined) { 
    if (issueUrl) {
      this.onIssueSelectClick.emit(issueUrl);
    }
  }
  clearFocusFilter() {
    this.selectedReleaseIssue = undefined;
    this.doReleaseIssueFilter(this.searchReleaseIssuesField.value || '');
    this.onSelectClick.emit(undefined);
    this.updateParams();
  }
  
  clearCategoryFilter() {
    this.categoryFilter = undefined;
    this.doReleaseIssueFilter(this.searchReleaseIssuesField.value || '');
    this.updateParams();
  }

  clearPriorityFilter() {
    this.priorityFilter = undefined;
    this.doReleaseIssueFilter(this.searchReleaseIssuesField.value || '');
    this.updateParams();
  }

  clearStatusFilter() {
    this.statusFilter = undefined;
    this.doReleaseIssueFilter(this.searchReleaseIssuesField.value || '');
    this.updateParams();
  }

  clearTagFilter() {
    this.tagFilter = undefined;
    this.doReleaseIssueFilter(this.searchReleaseIssuesField.value || '');
    this.updateParams();
  }

  clearHomeFilter() {
    this.homeFilter = undefined;
    this.doReleaseIssueFilter(this.searchReleaseIssuesField.value || '');
    this.updateParams();
  }

  downloadCSV() {
    if (!this.filteredReleaseIssues) {
      return;
    }

    const csvData = this.convertToCSV(this.filteredReleaseIssues);
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const timestamp = Utils.getCurrentTimestamp();
    const fileName = `release_issues_${timestamp}.csv`;

    // For IE 11
    if ((navigator as any).msSaveBlob) {
      (navigator as any).msSaveBlob(blob, fileName);
    } else {
      // Modern browsers
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  private convertToCSV(releaseItems: ReleaseIssue[]): string {
    let str = '';
    let row = '';

    const headers = ['Title', 'Description', 'Status', 'Priority', 'Category', 'Created', 'Created By', 'Assigned To'];
    row = headers.join(',') + '\r\n';
    str += row;

    releaseItems.forEach((item: ReleaseIssue) => {
      let line = '';
      line += `${item.title},`;
      line += `${item.description},`;
      line += `${this.releaseIssueStatuses[item?.releaseIssueStatus ?? 1]},`;
      line += `${this.getPriority(item?.releaseIssuePriority)},`;
      line += `${this.getCategory(item?.releaseIssueCategory)},`;
      line += `${item?.dateCreated},`;
      line += `${item?.createdByName ?? ""},`;
      line += `${item?.assignedToName ?? ""},`;
      str += line + '\r\n';
    });

    return str;
  }

  //tags
  resourceTypeEnum = ResourceType.Any;
  addTag(tag: Tag, releaseIssue: ReleaseIssue) {
    if (tag && releaseIssue) {
      let request = new AssignReleaseIssueTagRequest();
      request.companyId = this.selectedCompany?.id;
      request.tagId = tag.id;
      request.releaseIssueId = releaseIssue?.id;
  
      this.situViewApiClient.assignReleaseIssueTag(request)
        .then((response: AssignReleaseIssueTagResponse) => {
          if (response.success && releaseIssue?.releaseIssueTags) {
            let releaseIssueTag = new ReleaseIssueTag();
            releaseIssueTag.releaseIssueId = releaseIssue.id;
            releaseIssueTag.tagId = tag.id;
            releaseIssue?.releaseIssueTags.push(releaseIssueTag);
            
            // Update house report after adding tag
            this.processHouseReport();
          }
        });
    }
  }

  removeTag(tag: Tag, releaseIssue: ReleaseIssue) {
    if (tag && releaseIssue) {
      let request = new RemoveReleaseIssueTagRequest();
      request.companyId = this.selectedCompany?.id;
      request.tagId = tag.id;
      request.releaseIssueId = releaseIssue?.id;
  
      this.situViewApiClient.removeReleaseIssueTag(request)
        .then((response: RemoveReleaseIssueTagResponse) => {
          if (response.success && releaseIssue?.releaseIssueTags) {
            releaseIssue.releaseIssueTags = releaseIssue.releaseIssueTags?.filter(x => x.tagId !== tag.id);
            
            // Update house report after removing tag
            this.processHouseReport();
          }
        });
    }
  }

  tags: Tag[] | undefined;
  private loadTags() {
    let request = new ListTagsRequest();
    request.companyId = this.selectedCompany?.id;
    request.resourceType = this.resourceTypeEnum;

    return this.situViewApiClient.listTags(request)
      .then((response: ListTagsResponse) => {
        if (response.success) {
          this.tags = response.tags;
        }
      });
  }
  getAssignedTagIds(item: any): string[] {
    if (!item || !item.releaseIssueTags) {
      return [];
    }
    
    return item.releaseIssueTags.map((x: ReleaseIssueTag) => x.tagId || '');
  }
  //tags

  showMessage(message: string) {
    this.snackBar.open(message, 'Dismiss', {
      duration: 3000
    });
  }

  //stats board
  uniqueOpenIssues: string = "0";
  affectedHomes: string = "0";
  totalHomes: string = "0";
  issuesOpenLast7Days: string = "0";
  issuesClosedLast7Days: string = "0";
  issuesUnassigned: string = "0";
  private calculateStats() {

  }

  // Existing code for charts...
  renderChartLabels: string[] = ['Product A', 'Product B', 'Product C', 'Product D', 'Product E'];
  renderChartData: any[] = [
    { data: [65, 59, 80, 81, 56], label: 'Render Time (seconds)' }
  ];
  renderChartColors: string[] = ['rgba(63, 81, 181, 0.8)'];
  renderChartLoading: boolean = false;
  
  viewsChartLabels: string[] = ['Home 1', 'Home 2', 'Home 3', 'Home 4', 'Home 5'];
  viewsChartData: any[] = [
    { data: [421, 376, 290, 265, 203], label: 'Views (24h)' }
  ];
  viewsChartColors: string[] = ['rgba(76, 175, 80, 0.8)'];
  viewsChartLoading: boolean = false;
  
  selectedProduct: any = {
    product: 'Product C',
    productColor: 'rgba(63, 81, 181, 0.8)'
  };

  // Dark mode toggle
  isDarkMode: boolean = false;

  // Issue Status Pie Chart
  issueChartLoading: boolean = true;
  issueStatusLabels: string[] = ['Closed Issues', 'Open Issues'];
  issueStatusData: any[] = [];
  issueStatusColors: string[] = [ 'hsl(330, 90%, 65%)', 'hsl(330, 90%, 45%)' ];
  private calculateStatusPieChart() {
    let completeIssues = this.filteredReleaseIssues?.filter(x => x.releaseIssueStatus == ReleaseIssueStatus.Complete || x.releaseIssueStatus == ReleaseIssueStatus.CompleteNonIssue)?.length;
    let incompleteIssues = this.filteredReleaseIssues?.filter(x => x.releaseIssueStatus == ReleaseIssueStatus.InProgress || x.releaseIssueStatus == ReleaseIssueStatus.Assigned || x.releaseIssueStatus == ReleaseIssueStatus.Unassigned)?.length;
    this.uniqueOpenIssues = incompleteIssues?.toString() ?? "0";
    this.issueStatusData = [
      { data: [completeIssues, incompleteIssues], label: 'Issue Status' }
    ]
    const date7DaysAgo = new Date();
    date7DaysAgo.setDate(date7DaysAgo.getDate() - 7);
    this.issuesClosedLast7Days = this.filteredReleaseIssues?.filter(x => (x.releaseIssueStatus == ReleaseIssueStatus.Complete || x.releaseIssueStatus == ReleaseIssueStatus.CompleteNonIssue) && x.dateUpdated! > date7DaysAgo)?.length?.toString() ?? "0";
    this.issuesOpenLast7Days = this.filteredReleaseIssues?.filter(x => x.dateCreated! > date7DaysAgo)?.length?.toString() ?? "0";
    this.issuesUnassigned = this.filteredReleaseIssues?.filter(x => x.assignedTo === null)?.length?.toString() ?? "0";

    this.issueChartLoading = false;
  }

  // Other chart properties and methods remain the same...
  homesProgressChartLoading: boolean = true;
  homesProgressLabels: string[] = ['Homes Complete', 'Homes Remaining'];
  homesProgressData: any[] = [];
  homesProgressColors: string[] = [ 'hsl(210, 90%, 65%)', 'hsl(210, 90%, 45%)' ];
  homesRemainingPercentage: string = "0.0";
  private calculateHomeProgressPieChart() {
    if (!this.release?.releaseItems || !this.filteredReleaseIssues) {
      return;
    }
  
    // Get all unique homes
    const allHomes = new Set<string>();
    this.release.releaseItems.forEach(item => {
      if (item.home) {
        allHomes.add(item.home);
      }
    });
    
    
  
    // Get homes with issues
    const homesWithIssues = new Set<string>();
    this.filteredReleaseIssues.forEach(issue => {
      // Find all homes associated with this issue
      const associatedHomes = this.release?.releaseItems
        ?.filter(item => item.releaseIssueReleaseItems?.some(ri => ri.releaseIssueId === issue.id))
        ?.map(item => item.home)
        ?.filter(Boolean) as string[];
      
      // Add each home to the set of homes with issues
      associatedHomes.forEach(home => {
        if (home) {
          homesWithIssues.add(home);
        }
      });
    });
  
    // Calculate counts
    const homesWithIssuesCount = homesWithIssues.size;
    const homesWithoutIssuesCount = allHomes.size - homesWithIssuesCount;
    const totalHomes = allHomes.size;
    this.totalHomes = totalHomes?.toString() ?? "0";
    this.affectedHomes = homesWithIssuesCount?.toString() ?? "0";

    // Calculate the percentage of homes with issues
    let percentage = 0;
    if (totalHomes > 0) {
      percentage = (homesWithIssuesCount / totalHomes) * 100;
    }

    // Format the percentage with 1 decimal place
    this.homesRemainingPercentage = percentage.toFixed(1);
  
    // Update the chart data
    this.homesProgressData = [
      { 
        data: [homesWithoutIssuesCount, homesWithIssuesCount], 
        label: 'Homes Progress' 
      }
    ];
  
    // Set the chart labels (you might want to adjust these)
    this.homesProgressLabels = ['Homes Complete', 'Homes Remaining'];

    this.homesProgressChartLoading = false;
  }

  issueTypesLoading: boolean = true;
  issueTypesLabels: string[] = [];
  issueTypesData: any[] = [];
  issueTypesColors: string[] = [];

  priorityTypesLoading: boolean = true;
  priorityTypesLabels: string[] = ['Blocker', 'Medium', 'Low'];
  priorityTypesData: any[] = [];
  priorityTypesColors: string[] = [ 'hsl(145, 60%, 65%)', 'hsl(145, 60%, 55%)', 'hsl(145, 60%, 45%)' ];
  private calculatePriorityTypesPieChart() {
    if (!this.release?.releaseItems || !this.filteredReleaseIssues) {
      return;
    }

    const highPriority = this.filteredReleaseIssues.filter(x => x.releaseIssuePriority === ReleaseIssuePriority.High)?.length ?? 0;
    const mediumPriority = this.filteredReleaseIssues.filter(x => x.releaseIssuePriority === ReleaseIssuePriority.Medium)?.length ?? 0;
    const lowPriority = this.filteredReleaseIssues.filter(x => x.releaseIssuePriority === ReleaseIssuePriority.Low)?.length ?? 0;
    
    this.priorityTypesData = [
      { 
        data: [highPriority, mediumPriority, lowPriority], 
        label: 'Open issues by Priority' 
      }
    ];

    this.priorityTypesLoading = false;
  }

  releaseIssueCategoriesLoading: boolean = true;
  releaseIssueCategoriesLabels: string[] = ['Home Plan', 'Style / Theme', 'Software'];
  releaseIssueCategoriesData: any[] = [];
  releaseIssueCategoriesColors: string[] = [ 'hsl(30, 90%, 65%)', 'hsl(30, 90%, 55%)', 'hsl(30, 90%, 45%)' ];

  private calculateIssueCategoriesPieChart() {
    if (!this.release?.releaseItems || !this.filteredReleaseIssues) {
      return;
    }

    const homePlan = this.filteredReleaseIssues.filter(x => x.releaseIssueCategory === ReleaseIssueCategory.Drawing)?.length ?? 0;
    const styleTheme = this.filteredReleaseIssues.filter(x => x.releaseIssueCategory === ReleaseIssueCategory.Theming)?.length ?? 0;
    const software = this.filteredReleaseIssues.filter(x => x.releaseIssueCategory === ReleaseIssueCategory.Software)?.length ?? 0;
    
    this.releaseIssueCategoriesData = [
      { 
        data: [homePlan, styleTheme, software], 
        label: 'Open issues by Category' 
      }
    ];

    this.releaseIssueCategoriesLoading = false;
  }

  releaseIssueStatusesLoading: boolean = true;
  releaseIssueStatusesLabels: string[] = [];
  releaseIssueStatusesData: any[] = [];
  releaseIssueStatusesColors: string[] = [ 
    'hsl(280, 70%, 65%)',
    'hsl(280, 70%, 55%)', 
    'hsl(280, 70%, 45%)', 
    'hsl(280, 70%, 35%)', 
    'hsl(280, 70%, 25%)' 
  ];
  private calculateIssueStatusesPieChart() {
    if (!this.release?.releaseItems || !this.filteredReleaseIssues) {
      return;
    }
  
    // Create an object to hold counts for each status
    const statusCounts: { [key: number]: number } = {};
    const statusLabels: string[] = [];
    
    // Initialize counts for all possible status values in the enum
    Object.keys(this.releaseIssueStatuses)
      .filter(key => !isNaN(Number(key))) // Filter out reverse mappings
      .forEach(statusKey => {
        const statusValue = Number(statusKey);
        statusCounts[statusValue] = 0;
        
        // Get the string representation for this status
        const statusName = this.releaseIssueStatuses[statusValue];
        statusLabels.push(statusName);
      });
    
    // Count issues by status
    this.filteredReleaseIssues.forEach(issue => {
      if (issue.releaseIssueStatus !== undefined && statusCounts[issue.releaseIssueStatus] !== undefined) {
        statusCounts[issue.releaseIssueStatus]++;
      }
    });
    
    // Convert counts object to an array in the same order as the labels
    const statusData = Object.keys(statusCounts)
      .filter(key => !isNaN(Number(key)))
      .map(key => statusCounts[Number(key)]);
    
    // Update chart data
    this.releaseIssueStatusesData = [
      { 
        data: statusData, 
        label: 'Issues by Status' 
      }
    ];
    
    // Set chart labels
    this.releaseIssueStatusesLabels = statusLabels;
    
    this.releaseIssueStatusesLoading = false;
  }

  labelFormatter(label: string): string {
    return `${label} Issues`;
  }

  handleChartClick(label: string): void {
    //console.log(label);
    this.selectedProduct = {
      product: label,
      productColor: 'rgba(63, 81, 181, 0.8)'
    };
  }

  toggleDarkMode(): void {
    this.isDarkMode = !this.isDarkMode;
  }

  private updateParams() {
    const queryParams: { [key: string]: any } = {};

    // Release filter
    if (this.selectedReleaseId) {
      queryParams[this.ReleaseParamName] = this.selectedReleaseId;
    }

    // Priority filter
    if (this.priorityFilter !== undefined) {
      queryParams[this.PriorityParamName] = this.priorityFilter.toString();
    }

    // Status filter  
    if (this.statusFilter !== undefined) {
      queryParams[this.StatusParamName] = this.statusFilter.toString();
    }

    // Category filter
    if (this.categoryFilter !== undefined) {
      queryParams[this.CategoryParamName] = this.categoryFilter.toString();
    }

    // Tag filter
    if (this.tagFilter) {
      queryParams[this.TagParamName] = this.tagFilter;
    }

    // Created filter
    if (this.createdFilter) {
      queryParams[this.CreatedParamName] = this.createdFilter;
    }

    // Assigned filter
    if (this.assignedFilter) {
      queryParams[this.AssignedParamName] = this.assignedFilter;
    }

    // My Issues filter (only include if true)
    if (this.showMyIssuesOnly) {
      queryParams[this.MyIssuesParamName] = 'true';
    }

    // Open Only filter (only include if false, since true is default)
    if (!this.showOpenIssuesOnly) {
      queryParams[this.OpenOnlyParamName] = 'false';
    }

    // Search filter
    const searchValue = this.searchReleaseIssuesField?.value?.trim();
    if (searchValue) {
      queryParams[this.SearchParamName] = searchValue;
    }

    // Home filter
    if (this.homeFilter) {
      queryParams[this.HomeParamName] = this.homeFilter;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams
    });
  }

  // Copy current page URL to clipboard
  copyShareableUrl() {
    const currentUrl = window.location.href;
    
    if (navigator.clipboard && window.isSecureContext) {
      // Modern approach using Clipboard API
      navigator.clipboard.writeText(currentUrl).then(() => {
        this.showMessage('Shareable link copied to clipboard!');
      }).catch(err => {
        this.fallbackCopyTextToClipboard(currentUrl);
      });
    } else {
      // Fallback for older browsers
      this.fallbackCopyTextToClipboard(currentUrl);
    }
  }

  private fallbackCopyTextToClipboard(text: string) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.showMessage('Shareable link copied to clipboard!');
      } else {
        this.showMessage('Unable to copy link. Please copy manually from address bar.');
      }
    } catch (err) {
      this.showMessage('Unable to copy link. Please copy manually from address bar.');
    }
    
    document.body.removeChild(textArea);
  }

}